import { normalizePlate } from "./parsers";

// --- CONFIGURATION CONSTANTS (Could be moved to AppSettings later) ---
const RETENTION_CONFIG = {
  validFrazionamento: ["ANNUALE", "SEMESTRALE"],
  validCausale: ["POLIZZA IN SCADENZA"],
  excludedProducts: ["CASA FAMIGLIA", "INFORTUNI FAMIGLIA", "FAMIGLIA"],
  validRisk: ["RINNOVO"],
  validActions: ["EMISSIONE ANNUALE", "EMISSIONE SEMESTRALE"],
  windowDaysBefore: 30,
  windowDaysAfter: 15,
};

// --- HELPER FUNCTIONS ---

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const getDaysDifference = (d1, d2) => {
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const isDateInWindow = (prodDate, expiryDate, daysBefore, daysAfter) => {
  const minDate = new Date(expiryDate);
  minDate.setDate(minDate.getDate() - daysBefore);

  const maxDate = new Date(expiryDate);
  maxDate.setDate(maxDate.getDate() + daysAfter);

  // Reset hours to compare dates only
  minDate.setHours(0, 0, 0, 0);
  maxDate.setHours(23, 59, 59, 999);
  prodDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues

  return prodDate >= minDate && prodDate <= maxDate;
};

// --- CORE LOGIC ---

export const isEligibleExpiry = (row) => {
  const fraz = (row.frazionamento || "").trim().toUpperCase();
  const causale = (row.causale || "").trim().toUpperCase();
  const polizza = (row.numeroPolizza || "").trim().toUpperCase();
  // Product is not explicitly in ExpiryRow interface but might be there in raw data or inferred?
  // The user prompt mentions "eventuale collaboratore / cliente / prodotto" for Expiry file.
  // Assuming strict adherence to prompt: "exclude specific products".
  // However, ExpiryRow in types.ts doesn't have 'prodotto'.
  // I will check if I need to add it or if it's not available.
  // For now, I'll skip product check on expiry if field is missing, or check if row has it as any.

  if (!RETENTION_CONFIG.validFrazionamento.includes(fraz)) {
    return { isEligible: false, reason: `Frazionamento non valido: ${fraz}` };
  }

  if (!RETENTION_CONFIG.validCausale.includes(causale)) {
    return { isEligible: false, reason: `Causale non valida: ${causale}` };
  }

  if (polizza.includes("*")) {
    return { isEligible: false, reason: "Codice polizza contiene *" };
  }

  if (polizza.startsWith("PRP")) {
    return { isEligible: false, reason: "Codice polizza inizia con PRP" };
  }

  // Check for excluded products if the field exists (casting to any to access potential extra fields)
  const product = (row.prodotto || "").trim().toUpperCase();
  if (
    product &&
    RETENTION_CONFIG.excludedProducts.some((p) => product.includes(p))
  ) {
    return { isEligible: false, reason: `Prodotto escluso: ${product}` };
  }

  return { isEligible: true };
};

export const isValidProduction = (row) => {
  const risk = (row.rischio || "").trim().toUpperCase();
  const action = (row.tipoAzione || "").trim().toUpperCase();
  const polizza = (row.codicePolizza || "").trim().toUpperCase();
  const product = (row.prodotto || "").trim().toUpperCase();

  if (!RETENTION_CONFIG.validRisk.includes(risk)) {
    return { isValid: false, reason: `Rischio non valido: ${risk}` };
  }

  if (!RETENTION_CONFIG.validActions.includes(action)) {
    return { isValid: false, reason: `Azione non valida: ${action}` };
  }

  if (polizza.includes("*")) {
    return { isValid: false, reason: "Codice polizza contiene *" };
  }

  if (polizza.startsWith("PRP")) {
    return { isValid: false, reason: "Codice polizza inizia con PRP" };
  }

  if (RETENTION_CONFIG.excludedProducts.some((p) => product.includes(p))) {
    return { isValid: false, reason: `Prodotto escluso: ${product}` };
  }

  return { isValid: true };
};

export const calculateRetention = (
  production,
  expiries,
  settings, // For configurable window in future
) => {
  const windowBefore =
    settings?.targets?.retentionWindowDaysBefore ??
    RETENTION_CONFIG.windowDaysBefore; // Assuming we might add this to settings
  const windowAfter =
    settings?.targets?.retentionWindowDaysAfter ??
    RETENTION_CONFIG.windowDaysAfter;

  // 1. Prepare Production Data
  // Filter valid production rows first to optimize matching
  const validProduction = production.filter(
    (p) => isValidProduction(p).isValid,
  );

  // Group production by normalized reference for faster lookup
  const productionMap = new Map();
  validProduction.forEach((p) => {
    const normRef = normalizePlate(p.riferimento);
    if (!normRef) return;
    if (!productionMap.has(normRef)) {
      productionMap.set(normRef, []);
    }
    productionMap.get(normRef)?.push(p);
  });

  // 2. Process Expiries
  // Deduplication logic: "Per ogni evento reale di scadenza, considera una sola riga valida per combinazione almeno di: normalized_reference + Scadenza copertura"
  const uniqueExpiriesMap = new Map();
  const duplicatesCount = 0; // We need to track duplicates

  // We'll process all expiries first to enrich them, then handle deduplication
  let allEnrichedExpiries = expiries.map((exp) => {
    const normRef = normalizePlate(exp.riferimento);
    const eligibility = isEligibleExpiry(exp);

    return {
      ...exp,
      normalizedRef: normRef,
      isEligible: eligibility.isEligible,
      exclusionReason: eligibility.reason,
      retentionStatus: "NOT_RENEWED", // Default
    };
  });

  // 3. Deduplication of Expiries
  // Key: normalizedRef + scadenzaCopertura
  const uniqueEnrichedExpiries = [];
  const seenExpiryKeys = new Set();
  let duplicateCounter = 0;

  allEnrichedExpiries.forEach((exp) => {
    if (!exp.isEligible) {
      // If not eligible, we just keep it for reporting (with status EXCLUDED_EXPIRY)
      exp.retentionStatus = "EXCLUDED_EXPIRY";
      uniqueEnrichedExpiries.push(exp);
      return;
    }

    const key = `${exp.normalizedRef}|${exp.scadenzaCopertura}`;
    if (seenExpiryKeys.has(key)) {
      duplicateCounter++;
      // Mark as duplicate but maybe keep it? Prompt says "deduplica corretta o flag DUPLICATE"
      // I will add it but mark as DUPLICATE
      exp.retentionStatus = "DUPLICATE";
      exp.exclusionReason = "Duplicate expiry entry";
      uniqueEnrichedExpiries.push(exp);
    } else {
      seenExpiryKeys.add(key);
      uniqueEnrichedExpiries.push(exp);
    }
  });

  // 4. Matching Logic
  uniqueEnrichedExpiries.forEach((exp) => {
    if (!exp.isEligible || exp.retentionStatus === "DUPLICATE") return;

    const potentialMatches = productionMap.get(exp.normalizedRef);
    if (!potentialMatches || potentialMatches.length === 0) {
      exp.retentionStatus = "NOT_RENEWED";
      return;
    }

    const expDate = parseDate(exp.scadenzaCopertura);
    if (!expDate) {
      exp.retentionStatus = "MANUAL_CHECK";
      exp.exclusionReason = "Invalid expiry date";
      return;
    }

    // Filter matches within window
    const matchesInWindow = potentialMatches.filter((p) => {
      const pDate = parseDate(p.dataTransazione);
      if (!pDate) return false;
      return isDateInWindow(pDate, expDate, windowBefore, windowAfter);
    });

    if (matchesInWindow.length > 0) {
      // Find best match (closest date)
      matchesInWindow.sort((a, b) => {
        const dateA = parseDate(a.dataTransazione);
        const dateB = parseDate(b.dataTransazione);
        const diffA = Math.abs(dateA.getTime() - expDate.getTime());
        const diffB = Math.abs(dateB.getTime() - expDate.getTime());
        return diffA - diffB;
      });

      const bestMatch = matchesInWindow[0];
      const matchDate = parseDate(bestMatch.dataTransazione);

      exp.retentionStatus = "RENEWED";
      exp.matchedProductionRow = bestMatch;
      exp.retentionDetails = {
        status: "RENEWED",
        matchId: bestMatch.codicePolizza, // Using policy code as ID proxy
        matchDate: bestMatch.dataTransazione,
        matchDistance: getDaysDifference(expDate, matchDate),
      };
    } else {
      // Found matches but OUT OF WINDOW
      exp.retentionStatus = "OUT_OF_WINDOW";
      // We could attach the closest out-of-window match for info
      const closestOutOfWindow = potentialMatches.sort((a, b) => {
        const dateA = parseDate(a.dataTransazione);
        const dateB = parseDate(b.dataTransazione);
        if (!dateA || !dateB) return 0;
        return (
          Math.abs(dateA.getTime() - expDate.getTime()) -
          Math.abs(dateB.getTime() - expDate.getTime())
        );
      })[0];

      if (closestOutOfWindow) {
        const matchDate = parseDate(closestOutOfWindow.dataTransazione);
        if (matchDate) {
          exp.retentionDetails = {
            status: "OUT_OF_WINDOW",
            matchId: closestOutOfWindow.codicePolizza,
            matchDate: closestOutOfWindow.dataTransazione,
            matchDistance: getDaysDifference(expDate, matchDate),
          };
        }
      }
    }
  });

  // 5. Calculate Stats
  const eligibleRows = uniqueEnrichedExpiries.filter(
    (e) => e.isEligible && e.retentionStatus !== "DUPLICATE",
  );
  const renewedRows = eligibleRows.filter(
    (e) => e.retentionStatus === "RENEWED",
  );

  const stats = {
    totalEligible: eligibleRows.length,
    renewed: renewedRows.length,
    notRenewed: eligibleRows.length - renewedRows.length,
    retentionRate:
      eligibleRows.length > 0
        ? (renewedRows.length / eligibleRows.length) * 100
        : 0,

    excludedAsterisk: expiries.filter((e) =>
      (e.numeroPolizza || "").includes("*"),
    ).length,
    excludedPrp: expiries.filter((e) =>
      (e.numeroPolizza || "").startsWith("PRP"),
    ).length,
    excludedAction: 0, // Not applicable to expiries directly, but tracked in production validity
    excludedProduct: 0, // Hard to track without explicit product field in expiry

    outOfWindow: uniqueEnrichedExpiries.filter(
      (e) => e.retentionStatus === "OUT_OF_WINDOW",
    ).length,
    duplicates: duplicateCounter,
    manualChecks: uniqueEnrichedExpiries.filter(
      (e) => e.retentionStatus === "MANUAL_CHECK",
    ).length,

    totalPremiumExpiry: eligibleRows.reduce(
      (sum, e) => sum + (e.premioLordo || 0),
      0,
    ),
    totalPremiumRenewed: renewedRows.reduce(
      (sum, e) => sum + (e.matchedProductionRow?.premioNetto || 0),
      0,
    ),
    avgPremiumExpiry: 0,
    avgPremiumRenewed: 0,
  };

  if (stats.totalEligible > 0)
    stats.avgPremiumExpiry = stats.totalPremiumExpiry / stats.totalEligible;
  if (stats.renewed > 0)
    stats.avgPremiumRenewed = stats.totalPremiumRenewed / stats.renewed;

  return { enrichedExpiries: uniqueEnrichedExpiries, stats };
};
